import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Info, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ComboboxOption } from '@/components/ui/combobox';
import { MultiSelectCombobox } from '@/components/ui/multi-select-combobox';
import { DimensionFilterRow } from '@/components/DimensionFilterRow';
import type {
  CatalogDimension,
  CatalogItemState,
  CatalogMetric,
  RestrictionCheck,
  RowRestriction,
  UnreachablePolicy,
} from '@/services/userGroup.service';
import type { AnalysisDimensionFilter } from '@/utils/filterComparators';

/**
 * Editors shared by the group dialog and the user dialog: a user's own scope
 * and a group's scope have the same shape (row restrictions + hidden metrics).
 *
 * Scopes aren't branched, but metrics and dimensions are, so these take the
 * scope catalog (GET /user-groups/catalog) rather than the regular lists, which
 * follow Develop mode. Scopes may reference published items only: Develop-only
 * items are listed but disabled ("publish first"), connectivity is Production's,
 * and what the next publish would change is called out as a warning.
 */

const STATE_SUFFIX: Record<CatalogItemState, string> = {
  published: '',
  unpublished: ' (unpublished)',
  removed_in_develop: ' (removed in Develop)',
};

/** "Name", "Name (unpublished)", or "Name → New name in Develop". */
export function catalogLabel(item: Pick<CatalogDimension, 'name' | 'develop_name' | 'state'>): string {
  const renamed = item.develop_name ? ` → ${item.develop_name} in Develop` : '';
  return `${item.name}${renamed}${STATE_SUFFIX[item.state]}`;
}

/**
 * After a save: say which restrictions are waiting on the next publish and which
 * won't survive it. The save itself succeeded either way.
 */
export function notifyRestrictionChecks(checks: RestrictionCheck[] | undefined) {
  for (const check of checks ?? []) {
    if (check.state === 'breaks_on_publish' && check.message) {
      toast.warning(check.message, { duration: 12000 });
    }
  }
}

export function CatalogStateBadge({ state }: { state: CatalogItemState }) {
  if (state === 'published') return null;
  return (
    <Badge variant="outline" className="ml-2 text-[10px] font-normal">
      {state === 'unpublished' ? 'Unpublished' : 'Removed in Develop'}
    </Badge>
  );
}

function InfoNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function newRowRestriction(): RowRestriction {
  return { dimension_id: '', comparator: 'IN', value: [], applies_to_metric_ids: [], unreachable: 'deny' };
}

type AppliesTo = 'all' | 'specific';

function RestrictionEditor({
  restriction,
  onChange,
  onDelete,
  dimensionOptions,
  dimensionsById,
  metrics,
  hiddenMetricIds,
}: {
  restriction: RowRestriction;
  onChange: (r: RowRestriction) => void;
  onDelete: () => void;
  dimensionOptions: ComboboxOption[];
  dimensionsById: Map<string, CatalogDimension>;
  metrics: CatalogMetric[];
  hiddenMetricIds: string[];
}) {
  const dimension = dimensionsById.get(restriction.dimension_id);
  const dimensionName = dimension?.develop_name ?? dimension?.name ?? 'the selected dimension';
  // "Specific" with nothing picked yet still saves as every metric, so the choice
  // is held here until a metric is picked.
  const [pickingSpecific, setPickingSpecific] = useState(false);
  const appliesTo: AppliesTo = restriction.applies_to_metric_ids.length > 0 || pickingSpecific ? 'specific' : 'all';

  const filter: AnalysisDimensionFilter = {
    dimension_id: restriction.dimension_id,
    comparator: restriction.comparator,
    value: restriction.value as AnalysisDimensionFilter['value'],
  };

  // Connectivity is computed server-side (is_connected in src/services/user_groups/
  // service.py): a calculated metric counts only if every metric it's built from does.
  // The editor goes by how things will be after the next publish -- the state the
  // restriction has to keep working in.
  const did = restriction.dimension_id;
  const published = useMemo(() => metrics.filter((m) => m.state !== 'unpublished'), [metrics]);
  const connectedAfter = useCallback((m: CatalogMetric) => m.connected_after_publish.includes(did), [did]);
  const connectedNow = useCallback((m: CatalogMetric) => m.connected_now.includes(did), [did]);

  // Only metrics connected in Production can be named: that's where the restriction
  // is enforced. One connected only in Develop (or not published at all) is listed
  // but disabled until it's published; one that Develop disconnects or removes is
  // selectable but labelled, and the save and publish dialog say what happens to it.
  const connectedOptions: ComboboxOption[] = useMemo(
    () => metrics.filter((m) => connectedAfter(m) || connectedNow(m)).map((m) => {
      if (m.state === 'unpublished' || !connectedNow(m)) {
        return { value: m.id, label: `${catalogLabel(m)} (publish first)`, disabled: true };
      }
      const suffix = m.state === 'published' && !connectedAfter(m) ? ' (disconnected in Develop)' : '';
      return { value: m.id, label: catalogLabel(m) + suffix };
    }),
    [metrics, connectedAfter, connectedNow]
  );
  const removedSelected = useMemo(
    () => metrics.filter((m) => m.state === 'removed_in_develop' && restriction.applies_to_metric_ids.includes(m.id)),
    [metrics, restriction.applies_to_metric_ids]
  );
  const unconnected = useMemo(
    () => published
      .filter((m) => !connectedNow(m) && !hiddenMetricIds.includes(m.id))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [published, connectedNow, hiddenMetricIds]
  );
  const losingConnection = useMemo(
    () => metrics.filter((m) => m.state === 'published' && connectedNow(m) && !connectedAfter(m)),
    [metrics, connectedNow, connectedAfter]
  );
  const gainingConnection = useMemo(
    () => metrics.filter((m) => m.state === 'published' && !connectedNow(m) && connectedAfter(m)),
    [metrics, connectedNow, connectedAfter]
  );
  const hiddenSelected = useMemo(
    () => metrics.filter((m) => restriction.applies_to_metric_ids.includes(m.id) && hiddenMetricIds.includes(m.id)),
    [metrics, restriction.applies_to_metric_ids, hiddenMetricIds]
  );

  // Calculated metrics the restriction covers only part of: some of the metrics
  // they're built from are restricted and some aren't, so their numbers mix
  // restricted and unrestricted rows (e.g. GGR = Turnover - Payout with only
  // Turnover restricted). Flagged rather than fixed: whether to restrict the rest,
  // hide the calculated metric, or accept it is the admin's call.
  const partlyCovered = useMemo(() => {
    if (!dimension) return [];
    const byId = new Map(metrics.map((m) => [m.id, m]));
    let restricted: (baseId: string) => boolean;
    if (appliesTo === 'specific') {
      const covered = new Set<string>();
      for (const id of restriction.applies_to_metric_ids) {
        const m = byId.get(id);
        if (m?.is_calculation) m.base_metric_ids.forEach((b) => covered.add(b));
        else covered.add(id);
      }
      restricted = (b) => covered.has(b);
    } else if (restriction.unreachable === 'skip') {
      // Unconnected inputs are shown unrestricted; connected ones are restricted.
      restricted = (b) => !!byId.get(b)?.connected_now.includes(did);
    } else {
      return []; // 'deny' refuses the whole calculated metric instead of mixing.
    }
    return metrics
      .filter((m) => m.is_calculation && m.state !== 'removed_in_develop' && !hiddenMetricIds.includes(m.id))
      .map((m) => {
        const bases = m.base_metric_ids.map((b) => byId.get(b)).filter((b): b is CatalogMetric => !!b);
        return {
          metric: m,
          restricted: bases.filter((b) => restricted(b.id)),
          unrestricted: bases.filter((b) => !restricted(b.id)),
        };
      })
      .filter((x) => x.restricted.length > 0 && x.unrestricted.length > 0)
      .sort((a, b) => a.metric.name.localeCompare(b.metric.name));
  }, [dimension, metrics, appliesTo, restriction.applies_to_metric_ids, restriction.unreachable, did, hiddenMetricIds]);

  const handleFilterChange = (f: AnalysisDimensionFilter) => {
    const next = { ...restriction, dimension_id: f.dimension_id, comparator: f.comparator, value: f.value as RowRestriction['value'] };
    if (f.dimension_id !== restriction.dimension_id) {
      // Picked metrics must be connected to the dimension; drop any the new one doesn't reach.
      next.applies_to_metric_ids = restriction.applies_to_metric_ids.filter((id) => {
        const m = metrics.find((x) => x.id === id);
        return !!m && (m.connected_after_publish.includes(f.dimension_id) || m.connected_now.includes(f.dimension_id));
      });
    }
    onChange(next);
  };

  const handleAppliesToChange = (value: AppliesTo) => {
    setPickingSpecific(value === 'specific');
    // A restriction on named metrics needs no rule for unconnected ones: only
    // connected metrics can be named. Back on all metrics, hide them until the
    // admin chooses to show them.
    onChange({ ...restriction, applies_to_metric_ids: [], unreachable: 'deny' });
  };

  const names = (ms: CatalogMetric[]) => ms.map((m) => m.name).join(', ');

  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Only show rows where</Label>
        {/* Set apart from the settings below: this is the condition itself. The row's
            own divider and hover tint would read as a table inside a table. */}
        <div className="rounded-md border bg-muted/40 px-1 [&_tr]:border-0 [&_tr:hover]:bg-transparent">
          <Table>
            <TableBody>
              <DimensionFilterRow
                dimensionFilter={filter}
                onChange={handleFilterChange}
                onDelete={onDelete}
                dimensionOptions={dimensionOptions}
              />
            </TableBody>
          </Table>
        </div>
        {dimension?.state === 'unpublished' && (
          <p className="text-xs text-destructive">
            {dimensionName} isn't published yet. Publish it before restricting on it.
          </p>
        )}
        {dimension?.state === 'removed_in_develop' && (
          <p className="text-xs text-destructive">
            {dimension.name} is removed in Develop. After the next publish, members will get an error until this restriction is changed.
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Applies to</Label>
          <Select value={appliesTo} onValueChange={(v) => handleAppliesToChange(v as AppliesTo)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All metrics</SelectItem>
              <SelectItem value="specific">Specific metrics</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {appliesTo === 'specific' ? (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Metrics</Label>
            <MultiSelectCombobox
              options={connectedOptions}
              value={restriction.applies_to_metric_ids}
              onValueChange={(ids) => onChange({ ...restriction, applies_to_metric_ids: ids })}
              placeholder={dimension ? 'Select metrics...' : 'Pick a dimension first'}
              disabled={!dimension}
            />
          </div>
        ) : (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Metrics not connected to this dimension</Label>
            <Select
              value={restriction.unreachable}
              onValueChange={(v) => onChange({ ...restriction, unreachable: v as UnreachablePolicy })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deny">Hide them</SelectItem>
                <SelectItem value="skip">Show them (they don't contain this data)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {partlyCovered.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600" />
          <div className="space-y-1">
            <p className="font-medium">
              {partlyCovered.length === 1 ? '1 calculated metric is' : `${partlyCovered.length} calculated metrics are`} only
              partly covered by this restriction
            </p>
            <ul className="space-y-0.5">
              {partlyCovered.map(({ metric, restricted, unrestricted }) => (
                <li key={metric.id}>
                  <span className="font-medium">{metric.name}</span> is built from{' '}
                  {restricted.map((b) => b.name).join(', ')} (restricted) and{' '}
                  {unrestricted.map((b) => b.name).join(', ')} (
                  {appliesTo === 'specific' ? 'not restricted' : `not connected to ${dimensionName}, so not restricted`}).
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground">
              Their numbers mix restricted and unrestricted rows, so they won't add up for members.{' '}
              {appliesTo === 'specific'
                ? 'Add the other metrics to this restriction, or hide the calculated metric under Metric Visibility.'
                : 'Connect the other metrics to this dimension, choose "Hide them", or hide the calculated metric under Metric Visibility.'}
            </p>
          </div>
        </div>
      )}
      {appliesTo === 'specific' ? (
        <InfoNote>
          <p>
            Only metrics connected to {dimensionName} are listed, since the restriction can only filter a metric
            that can be broken down by it. A calculated metric is listed only if every metric it's built from is
            connected. Metrics marked "publish first" are only connected in Develop so far; publish to use them.
          </p>
          {restriction.applies_to_metric_ids.length === 0 && (
            <p className="text-amber-600">No metrics picked yet. Until you pick some, this restriction applies to all metrics.</p>
          )}
          {hiddenSelected.length > 0 && (
            <p>
              {names(hiddenSelected)} {hiddenSelected.length === 1 ? 'is' : 'are'} hidden
              under Metric Visibility, so this restriction only affects calculated metrics built from{' '}
              {hiddenSelected.length === 1 ? 'it' : 'them'}.
            </p>
          )}
          {removedSelected.length > 0 && (
            <p className="text-amber-600">
              {names(removedSelected)} {removedSelected.length === 1 ? 'is' : 'are'} removed in Develop. The restriction
              keeps working until the next publish; after it, {removedSelected.length === 1 ? 'that metric is' : 'those metrics are'} gone
              and the restriction no longer covers {removedSelected.length === 1 ? 'it' : 'them'}.
            </p>
          )}
          {losingConnection.some((m) => m.state === 'published' && restriction.applies_to_metric_ids.includes(m.id)) && (
            <p className="text-destructive">
              {names(losingConnection.filter((m) => restriction.applies_to_metric_ids.includes(m.id)))} lose their
              connection to {dimensionName} in Develop. After the next publish members won't be able to see them.
            </p>
          )}
        </InfoNote>
      ) : (
        <InfoNote>
          <p>
            A metric isn't connected to a dimension when it has no join path to it. Usually that's because
            breaking the metric down by this dimension doesn't make sense (it doesn't contain this data), or
            because the connection hasn't been configured yet.{' '}
            {restriction.unreachable === 'skip'
              ? 'With "Show them", those metrics are shown without this restriction.'
              : 'With "Hide them", they can\'t be seen at all.'}
          </p>
          {dimension && (unconnected.length === 0 ? (
            <p>Every metric is connected to {dimensionName}.</p>
          ) : (
            <p>
              Not connected to {dimensionName} ({unconnected.length}):{' '}
              <span className="text-foreground">{unconnected.map(catalogLabel).join(', ')}</span>
            </p>
          ))}
          {dimension && losingConnection.length > 0 && (
            <p className="text-amber-600">
              Lose their connection at the next publish: {names(losingConnection)}.
            </p>
          )}
          {dimension && gainingConnection.length > 0 && (
            <p>Connected from the next publish (shown without this restriction until then): {names(gainingConnection)}.</p>
          )}
        </InfoNote>
      )}
    </div>
  );
}

export function RowRestrictionsEditor({
  restrictions,
  onChange,
  dimensions,
  metrics,
  hiddenMetricIds,
}: {
  restrictions: RowRestriction[];
  onChange: (restrictions: RowRestriction[]) => void;
  dimensions: CatalogDimension[];
  metrics: CatalogMetric[];
  /** The same group's/user's Metric Visibility, to explain overlap with a restriction. */
  hiddenMetricIds: string[];
}) {
  const dimensionOptions: ComboboxOption[] = useMemo(
    () => dimensions.map((d) => (d.state === 'unpublished'
      ? { value: d.id, label: `${catalogLabel(d)} (publish first)`, disabled: true }
      : { value: d.id, label: catalogLabel(d) })),
    [dimensions]
  );
  const dimensionsById = useMemo(() => new Map(dimensions.map((d) => [d.id, d])), [dimensions]);

  const setAt = useCallback((idx: number, r: RowRestriction | null) => {
    const next = [...restrictions];
    if (r) next[idx] = r;
    else next.splice(idx, 1);
    onChange(next);
  }, [restrictions, onChange]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Limit the rows shown, for all metrics or specific ones.
      </p>
      {restrictions.map((r, idx) => (
        <RestrictionEditor
          key={idx}
          restriction={r}
          onChange={(next) => setAt(idx, next)}
          onDelete={() => setAt(idx, null)}
          dimensionOptions={dimensionOptions}
          dimensionsById={dimensionsById}
          metrics={metrics}
          hiddenMetricIds={hiddenMetricIds}
        />
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...restrictions, newRowRestriction()])}>
        <Plus className="h-4 w-4 mr-1" /> Add restriction
      </Button>
    </div>
  );
}

export function MetricVisibilityEditor({
  metrics,
  hiddenMetricIds,
  onChange,
  isLoading,
}: {
  metrics: Pick<CatalogMetric, 'id' | 'name' | 'develop_name' | 'state'>[];
  hiddenMetricIds: string[];
  onChange: (hiddenMetricIds: string[]) => void;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Loading...</div>;
  }
  if (!metrics.length) {
    return <div className="py-8 text-center text-muted-foreground">No metrics configured.</div>;
  }
  return (
    <div className="space-y-2">
    <p className="text-sm text-muted-foreground">
      Metrics added later are visible unless hidden here. Hiding a metric also hides every metric calculated from
      it. Metrics that aren't published yet can be hidden once they are.
    </p>
    <div className="max-h-[400px] overflow-y-auto border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">Visible</TableHead>
            <TableHead>Metric</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {metrics.map((m) => (
            <TableRow key={m.id}>
              <TableCell>
                <Checkbox
                  checked={!hiddenMetricIds.includes(m.id)}
                  // Unpublished metrics can be un-hidden but not newly hidden: the
                  // save would be rejected (publish first).
                  disabled={m.state === 'unpublished' && !hiddenMetricIds.includes(m.id)}
                  onCheckedChange={(checked) => onChange(
                    checked ? hiddenMetricIds.filter((id) => id !== m.id) : [...hiddenMetricIds, m.id]
                  )}
                />
              </TableCell>
              <TableCell>
                {m.name}
                {m.develop_name && <span className="text-muted-foreground"> → {m.develop_name} in Develop</span>}
                <CatalogStateBadge state={m.state} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
}
